import React, { useCallback, useEffect, useState } from 'react'
import { useGroupStore } from '@/stores/useGroupStore'
import { PinnedMessage as PinnedMessageType } from '@/api/types/group'
import { MessageContentData, MessageInfo, SessionType } from '@/api/types/chat'
import { formatHumanTime } from '@/utils/date-utils'
import { toast } from 'react-hot-toast'
import { useChatActions } from '@/hooks/useChatActions'
import { tabStateManager } from '@/utils/tabStateManager'
import MessageUtils from '@/utils/ably-im/message-utils'
import { logPrint } from '@/utils/my-utils'

interface PinnedMessageProps {
  conversationId: string
  sessionType: SessionType
  showUnpinButton?: boolean
}

/**
 * 群置顶消息组件
 * 悬浮在消息列表顶部显示置顶消息
 */
export default function PinnedMessage({
  conversationId,
  sessionType,
  showUnpinButton = true
}: PinnedMessageProps): React.JSX.Element | null {
  const [isUnpinning, setIsUnpinning] = useState(false)
  const [maxLine, setMaxLine] = useState(2)
  const [contentText, setContentText] = useState('')
  
  // 获取群组信息
  const groupItem = useGroupStore((state) => state.groupMap.get(conversationId))
  const groupInfo = useGroupStore((state) => state.groupInfoMap.get(groupItem?.id || ''))
  
  const chatActions = useChatActions()

  // Define function before using it in useEffect
  /**
   * 渲染置顶消息内容，解密内容
   */
  const renderPinnedMessageContent = useCallback(async (pinnedMessage: PinnedMessageType) => {
    try {
      // 如果没有直接内容，尝试从 message 对象中获取
      if (pinnedMessage.message) {
        const message = pinnedMessage.message

        // 显示发送者信息
        const senderName = message.sender?.nickname || ''

        // 如果有未加密的内容
        if (message.content) {
          //解密消息内容
          const encryptionKey = tabStateManager.getEncryptionKey();

          // 解密消息内容
          if (encryptionKey) {
            const decryptedContent = await MessageUtils.decryptMessageContent(message as unknown as MessageInfo, encryptionKey);
            logPrint("解密后的内容： ", decryptedContent)
            const contentData = JSON.parse(decryptedContent) as MessageContentData;
            return `${senderName}: ${contentData.content}`
          }
        }
      }

      return '置顶消息内容'
    } catch (error) {
      console.error('解析置顶消息失败:', error)
      return '消息内容解析失败'
    }
  }, [])

  // Always call useEffect before conditional returns
  const pinnedMessage = groupInfo?.pinned_message

  useEffect(() => {
    if (pinnedMessage) {
      renderPinnedMessageContent(pinnedMessage)
        .then(rep => {
          setContentText(rep)
        })
    }
  }, [pinnedMessage, renderPinnedMessageContent])

  // 只有群聊才显示置顶消息
  if (sessionType !== SessionType.Group) {
    return null
  }

  // 如果没有置顶消息，不显示
  if (!pinnedMessage) {
    return null
  }

  /**
   * 处理取消置顶
   */
  const handleUnpin = async (e: React.MouseEvent) => {
    e.stopPropagation() // 阻止事件冒泡

    if (!groupItem?.id || !pinnedMessage) {
      toast.error('群组信息不完整')
      return
    }
    setIsUnpinning(true)

    await chatActions.unpinGroupMessage(conversationId, groupItem.id, pinnedMessage.id)

    setIsUnpinning(false)
  }

  return (
    <div className="sticky top-0 z-10 bg-gradient-to-r from-yellow-50 to-orange-50 border-b border-yellow-200 shadow-sm">
      <div className="px-6 py-3">
        <div
          className="flex items-center space-x-3 cursor-pointer hover:bg-yellow-100 rounded-lg p-2 -m-2 transition-colors"
        >
          {/* 置顶图标 */}
          <div className="flex-shrink-0">
            <svg className="w-5 h-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
          </div>

          {/* 消息内容 */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center space-x-2">
                <span className="text-xs font-medium text-yellow-800">
                  📌 置顶消息
                </span>
                <span className="text-xs text-yellow-600">
                  由 {pinnedMessage.pinned_by?.nickname || '未知用户'} 置顶
                </span>
                {pinnedMessage.message?.timestamp && (
                  <span className="text-xs text-yellow-600">
                    {formatHumanTime(pinnedMessage.message.timestamp)}
                  </span>
                )}
              </div>
            </div>

            <div className={`text-sm text-gray-700 ${maxLine === 2 ? 'line-clamp-2' : ''} max-h-[500px] overflow-y-auto`}>
              {contentText}
            </div>
          </div>

          {/* 取消置顶按钮和箭头图标 */}
          <div className="flex items-center space-x-2">
            {showUnpinButton && (
              <button
                onClick={handleUnpin}
                disabled={isUnpinning}
                className="p-1 text-yellow-600 hover:text-yellow-800 hover:bg-yellow-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="取消置顶"
              >
                {isUnpinning ? (
                  <div className="w-4 h-4 animate-spin border-2 border-yellow-600 border-t-transparent rounded-full" />
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </button>
            )}
            <button
              onClick={() => setMaxLine(maxLine === 2 ? 100 : 2)}
              className="p-1 text-yellow-600 hover:text-yellow-800 hover:bg-yellow-100 rounded transition-colors"
              title={maxLine === 2 ? "展开更多" : "收起内容"}
            >
              {maxLine === 2 ? "▼" : "▲"}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
